/**
 * Client-side pre-publish validation (COMMUNICATION_DESIGN.md §8.3).
 *
 * Mirrors the backend TemplateValidationService so the Publish dialog can block
 * before the request is made and point at the offending token. The backend
 * remains authoritative — this is a fast-feedback gate, never the only check.
 */

export interface ValidationIssue {
  rule: string;
  message: string;
  token?: string;
}

export interface ValidationResult {
  valid: boolean;
  issues: ValidationIssue[];
}

const VARIABLE_PATTERN = /\{\{\s*([^}\s]+)\s*\}\}/g;

export function extractVariables(...sources: string[]): string[] {
  const found: string[] = [];
  for (const src of sources) {
    if (!src) continue;
    for (const match of src.matchAll(VARIABLE_PATTERN)) {
      const key = match[1];
      if (key) found.push(key);
    }
  }
  return found;
}

/** Unbalanced or malformed braces that the variable pattern would silently skip. */
function findBrokenTokens(source: string): string[] {
  if (!source) return [];
  const broken: string[] = [];
  // An opening {{ with no closing }} before the next opening or end of string.
  for (const match of source.matchAll(/\{\{(?:(?!\}\})[\s\S]){0,80}?(?=\{\{|$)/g)) {
    const text = match[0];
    if (!text.includes('}}')) broken.push(text.slice(0, 40).trim());
  }
  return broken;
}

/** Tags opened and never closed (ignoring void elements). */
function findUnclosedTags(html: string): string[] {
  if (!html) return [];
  const VOID = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
  ]);
  const stack: string[] = [];
  for (const m of html.matchAll(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/?)>/g)) {
    const raw = m[0];
    const name = (m[1] ?? '').toLowerCase();
    if (VOID.has(name) || m[2] === '/') continue;
    if (raw.startsWith('</')) {
      const idx = stack.lastIndexOf(name);
      if (idx !== -1) stack.splice(idx, 1);
    } else {
      stack.push(name);
    }
  }
  return stack;
}

export function validateTemplate(input: {
  subject: string;
  htmlBody: string;
  plainTextBody: string;
  knownVariableKeys: string[];
}): ValidationResult {
  const issues: ValidationIssue[] = [];
  const { subject, htmlBody, plainTextBody, knownVariableKeys } = input;

  if (!subject.trim()) {
    issues.push({ rule: 'Required content', message: 'Subject must not be empty.' });
  }
  if (!htmlBody.trim()) {
    issues.push({ rule: 'Required content', message: 'HTML body must not be empty.' });
  }

  // Unknown variables — not in the registry, so they will never resolve.
  const used = extractVariables(subject, htmlBody, plainTextBody);
  const known = new Set(knownVariableKeys);
  for (const key of new Set(used)) {
    if (!known.has(key)) {
      issues.push({
        rule: 'Unknown variable',
        message: `{{${key}}} is not registered and will render empty.`,
        token: key,
      });
    }
  }

  // Duplicate variables within the subject specifically — almost always a mistake
  // (the body legitimately repeats variables such as a name).
  const subjectVars = extractVariables(subject);
  const seen = new Set<string>();
  for (const key of subjectVars) {
    if (seen.has(key)) {
      issues.push({
        rule: 'Duplicate variable',
        message: `{{${key}}} appears more than once in the subject.`,
        token: key,
      });
    }
    seen.add(key);
  }

  // Malformed variable syntax.
  for (const src of [subject, htmlBody, plainTextBody]) {
    for (const token of findBrokenTokens(src)) {
      issues.push({
        rule: 'Broken variable syntax',
        message: `Unclosed variable near "${token}".`,
        token,
      });
    }
  }

  // Broken HTML.
  const unclosed = findUnclosedTags(htmlBody);
  if (unclosed.length > 0) {
    issues.push({
      rule: 'Broken HTML',
      message: `Unclosed tag(s): ${[...new Set(unclosed)].map((t) => `<${t}>`).join(', ')}.`,
    });
  }

  return { valid: issues.length === 0, issues };
}

/** Variable delta between the published version and the pending content. */
export function diffVariables(
  previous: string[],
  next: string[],
): { added: string[]; removed: string[] } {
  const prev = new Set(previous);
  const nxt = new Set(next);
  return {
    added: [...nxt].filter((k) => !prev.has(k)).sort(),
    removed: [...prev].filter((k) => !nxt.has(k)).sort(),
  };
}
